/**
 * ============================================================================
 * Invoice Management Page
 * ============================================================================
 * Version: 1.0.0
 * Created: 2024-12-17
 * 
 * Features:
 * - Invoice list with filters (status, payment status, customer, date range)
 * - Invoice detail view with items and tax breakdown
 * - PDF download
 * - CSV export (bulk)
 * - Edit draft invoices (limited fields)
 * - Responsive design
 * 
 * Integration:
 * - Calls /api/v1/invoices endpoints
 * - Links to allocation module
 * - PDF viewer
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Tooltip,
    Card,
    CardContent,
    Divider,
    Stack
} from '@mui/material';
import {
    Visibility,
    PictureAsPdf,
    Email,
    FileDownload,
    Edit,
    FilterList,
    Refresh
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export default function InvoiceManagement() {
    // State
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [total, setTotal] = useState(0);

    // Filters
    const [filters, setFilters] = useState({
        status: '',
        payment_status: '',
        customer_id: '',
        from_date: '',
        to_date: '',
        exported_to_zoho: null
    });

    // Detail dialog
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [invoiceItems, setInvoiceItems] = useState([]);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editData, setEditData] = useState({
        notes: '',
        payment_terms: '',
        due_date: ''
    });

    // Load invoices
    useEffect(() => {
        loadInvoices();
    }, [page, rowsPerPage, filters]);

    const loadInvoices = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: page + 1,
                limit: rowsPerPage,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
                )
            };

            const response = await axios.get(`${API_BASE_URL}/api/v1/invoices`, {
                params,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`
                }
            });

            setInvoices(response.data.items);
            setTotal(response.data.total);
            setLoading(false);
        } catch (err) {
            console.error('Failed to load invoices:', err);
            setError(err.response?.data?.detail || 'Failed to load invoices');
            setLoading(false);
        }
    };

    // View invoice details
    const handleViewDetails = async (invoice) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/v1/invoices/${invoice.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`
                    }
                }
            );

            setSelectedInvoice(response.data.invoice);
            setInvoiceItems(response.data.items);
            setDetailOpen(true);
        } catch (err) {
            console.error('Failed to load invoice details:', err);
            setError(err.response?.data?.detail || 'Failed to load invoice details');
        }
    };

    // Download PDF
    const handleDownloadPDF = async (invoice) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/v1/invoices/${invoice.id}/pdf`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`
                    },
                    responseType: 'blob'
                }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoice.invoice_number.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Failed to download PDF:', err);
            setError(err.response?.data?.detail || 'Failed to download PDF');
        }
    };

    // Edit invoice
    const handleEdit = (invoice) => {
        setSelectedInvoice(invoice);
        setEditData({
            notes: invoice.notes || '',
            payment_terms: invoice.payment_terms || '',
            due_date: invoice.due_date || ''
        });
        setEditOpen(true);
    };

    const handleSaveEdit = async () => {
        try {
            await axios.put(
                `${API_BASE_URL}/api/v1/invoices/${selectedInvoice.id}`,
                editData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`
                    }
                }
            );

            setEditOpen(false);
            loadInvoices();
        } catch (err) {
            console.error('Failed to update invoice:', err);
            setError(err.response?.data?.detail || 'Failed to update invoice');
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            draft: 'warning',
            sent: 'info',
            paid: 'success',
            overdue: 'error',
            cancelled: 'default'
        };
        return colors[status] || 'default';
    };

    const getPaymentStatusColor = (status) => {
        const colors = {
            unpaid: 'warning',
            partial: 'info',
            paid: 'success'
        };
        return colors[status] || 'default';
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Invoice Management</Typography>
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={() => setFilters({
                            status: '',
                            payment_status: '',
                            customer_id: '',
                            from_date: '',
                            to_date: '',
                            exported_to_zoho: null
                        })}
                    >
                        Clear Filters
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={loadInvoices}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Filters</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="sent">Sent</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="overdue">Overdue</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment Status</InputLabel>
                            <Select
                                value={filters.payment_status}
                                label="Payment Status"
                                onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="unpaid">Unpaid</MenuItem>
                                <MenuItem value="partial">Partial</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="From Date"
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="To Date"
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Invoice List */}
            <Paper>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Invoice #</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Payment</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((invoice) => (
                                        <TableRow key={invoice.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {invoice.invoice_number}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {format(new Date(invoice.invoice_date), 'MMM dd, yyyy')}
                                            </TableCell>
                                            <TableCell>{invoice.customer_name || invoice.customer_id}</TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatCurrency(invoice.total_amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={invoice.status.toUpperCase()}
                                                    color={getStatusColor(invoice.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={invoice.payment_status.toUpperCase()}
                                                    color={getPaymentStatusColor(invoice.payment_status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(invoice)}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Download PDF">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDownloadPDF(invoice)}
                                                    >
                                                        <PictureAsPdf />
                                                    </IconButton>
                                                </Tooltip>
                                                {invoice.status === 'draft' && (
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEdit(invoice)}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Paper>

            {/* Detail Dialog */}
            <Dialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Invoice Details - {selectedInvoice?.invoice_number}
                </DialogTitle>
                <DialogContent>
                    {selectedInvoice && (
                        <Box>
                            {/* Invoice Header */}
                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Customer</Typography>
                                    <Typography variant="body1">{selectedInvoice.customer_name}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                                    <Typography variant="body1">
                                        {format(new Date(selectedInvoice.invoice_date), 'MMM dd, yyyy')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Due Date</Typography>
                                    <Typography variant="body1">
                                        {selectedInvoice.due_date
                                            ? format(new Date(selectedInvoice.due_date), 'MMM dd, yyyy')
                                            : 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Payment Terms</Typography>
                                    <Typography variant="body1">{selectedInvoice.payment_terms || 'N/A'}</Typography>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Items Table */}
                            <Typography variant="h6" gutterBottom>Items</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell>HSN</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Rate</TableCell>
                                            <TableCell align="right">Tax</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoiceItems.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{item.item_name}</TableCell>
                                                <TableCell>{item.hsn_code || 'N/A'}</TableCell>
                                                <TableCell align="right">
                                                    {parseFloat(item.quantity)} {item.unit}
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                                                <TableCell align="right">
                                                    {parseFloat(item.tax_rate)}%
                                                </TableCell>
                                                <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Divider sx={{ my: 2 }} />

                            {/* Tax Summary */}
                            <Box display="flex" justifyContent="flex-end">
                                <Box width="300px">
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography>Subtotal:</Typography>
                                        <Typography>{formatCurrency(selectedInvoice.subtotal)}</Typography>
                                    </Box>
                                    {parseFloat(selectedInvoice.cgst) > 0 && (
                                        <>
                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                <Typography>CGST:</Typography>
                                                <Typography>{formatCurrency(selectedInvoice.cgst)}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                <Typography>SGST:</Typography>
                                                <Typography>{formatCurrency(selectedInvoice.sgst)}</Typography>
                                            </Box>
                                        </>
                                    )}
                                    {parseFloat(selectedInvoice.igst) > 0 && (
                                        <Box display="flex" justifyContent="space-between" mb={1}>
                                            <Typography>IGST:</Typography>
                                            <Typography>{formatCurrency(selectedInvoice.igst)}</Typography>
                                        </Box>
                                    )}
                                    <Divider sx={{ my: 1 }} />
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="h6">Total:</Typography>
                                        <Typography variant="h6">{formatCurrency(selectedInvoice.total_amount)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdf />}
                        onClick={() => handleDownloadPDF(selectedInvoice)}
                    >
                        Download PDF
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Invoice - {selectedInvoice?.invoice_number}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Notes"
                            multiline
                            rows={3}
                            value={editData.notes}
                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Payment Terms"
                            value={editData.payment_terms}
                            onChange={(e) => setEditData({ ...editData, payment_terms: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Due Date"
                            type="date"
                            value={editData.due_date}
                            onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
